
import React from 'react';
import Button from '../../../components/base/Button';

const ServicesSection: React.FC = () => {
  const services = [
    {
      icon: 'ri-stethoscope-line',
      title: 'Virtual Primary Care',
      description: 'Comprehensive subscription-based primary care with guaranteed 24-hour access to our medical team. Enjoy unlimited consultations, priority scheduling, and exclusive perks with all our specialized treatments.',
      features: ['24/7 On-Demand Access', 'Unlimited Consultations', 'Priority Treatment Scheduling', 'Exclusive Member Discounts'],
      image: 'https://public.readdy.ai/ai/img_res/535da04d4c215adb101baf3102e5b863.jpg',
      isSubscription: true,
      price: '$199/month'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Bioidentical Hormone Optimization',
      description: 'Restore hormonal balance with personalized bioidentical hormone replacement therapy. Our expert physicians create customized treatment plans to optimize your energy, mood, and overall vitality.',
      features: ['Testosterone Optimization', 'Estrogen Balance', 'Thyroid Support', 'Growth Hormone'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20medical%20consultation%20for%20hormone%20therapy%2C%20doctor%20explaining%20bioidentical%20hormone%20treatment%20to%20patient%2C%20modern%20medical%20office%20setting%2C%20clean%20white%20background%2C%20medical%20charts%20and%20hormone%20molecules%20visualization%2C%20professional%20healthcare%20environment&width=600&height=400&seq=hormone-therapy&orientation=landscape'
    },
    {
      icon: 'ri-dna-line',
      title: 'Advanced Peptide Therapy',
      description: "Unlock your body\\'s potential with cutting-edge peptide treatments designed to enhance performance, accelerate healing, and promote anti-aging benefits.",
      features: ['Anti-Aging Peptides', 'Performance Enhancement', 'Healing Acceleration', 'Cognitive Support'],
      image: 'https://readdy.ai/api/search-image?query=Advanced%20peptide%20therapy%20laboratory%20with%20molecular%20structures%2C%20scientific%20research%20environment%2C%20peptide%20vials%20and%20medical%20equipment%2C%20clean%20modern%20laboratory%20setting%2C%20molecular%20biology%20visualization%2C%20professional%20medical%20research%20facility&width=600&height=400&seq=peptide-therapy&orientation=landscape'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'NAD+ Cellular Regeneration',
      description: 'Boost cellular energy and promote longevity with NAD+ therapy. This revolutionary treatment supports cellular repair, enhances mental clarity, and increases overall energy levels.',
      features: ['Cellular Repair', 'Energy Boost', 'Mental Clarity', 'Longevity Support'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20cellular%20regeneration%20therapy%2C%20cellular%20biology%20visualization%2C%20mitochondria%20and%20cellular%20energy%2C%20modern%20medical%20treatment%20room%2C%20IV%20therapy%20setup%2C%20clean%20medical%20environment%20with%20cellular%20graphics&width=600&height=400&seq=nad-therapy&orientation=landscape'
    },
    {
      icon: 'ri-drop-line',
      title: 'IV Vitamin & Glutathione Therapy',
      description: 'Maximize nutrient absorption with customized IV vitamin cocktails and powerful glutathione treatments for detoxification and immune system support.',
      features: ['Custom Vitamin Blends', 'Immune Support', 'Detoxification', 'Energy Enhancement'],
      image: 'https://readdy.ai/api/search-image?query=IV%20vitamin%20therapy%20treatment%20room%2C%20medical%20professional%20administering%20IV%20drip%2C%20clean%20modern%20medical%20facility%2C%20vitamin%20bottles%20and%20medical%20equipment%2C%20professional%20healthcare%20setting%2C%20sterile%20medical%20environment&width=600&height=400&seq=iv-therapy&orientation=landscape'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Revolutionary <span className="text-blue-600">Treatment Options</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the future of healthcare with our comprehensive range of innovative 
            <strong> telemedicine</strong> treatments designed to optimize your health and well-being.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-dna-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              Bioidentical Hormone Optimization
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Restore hormonal balance with personalized bioidentical hormone therapy protocols.
            </p>
            <a 
              href="/services/hormone-optimization"
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
            >
              Learn More
            </a>
          </div>

          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-medicine-bottle-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              Advanced Peptide Therapy
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Enhance recovery, performance, and longevity with cutting-edge peptide protocols.
            </p>
            <a 
              href="/services/peptide-therapy"
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
            >
              Learn More
            </a>
          </div>

          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-flashlight-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              NAD+ Cellular Therapy
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Restore cellular energy and slow aging with revolutionary NAD+ infusion therapy.
            </p>
            <a 
              href="/services/nad-treatment"
              className="inline-block bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
            >
              Learn More
            </a>
          </div>

          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-drop-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              IV Vitamin Therapy
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Boost energy and immunity with premium IV vitamin and nutrient infusions.
            </p>
            <a 
              href="/services/iv-vitamin-therapy"
              className="inline-block bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
            >
              Learn More
            </a>
          </div>

          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-heart-pulse-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              Comprehensive Lab Testing
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Advanced biomarker analysis to optimize your health and treatment protocols.
            </p>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Learn More
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl flex items-center justify-center mb-4 lg:mb-6">
              <i className="ri-user-heart-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
              Personalized Wellness Plans
            </h3>
            <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
              Customized health optimization strategies tailored to your unique needs.
            </p>
            <button className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Learn More
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;
